import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@material-ui/core';
import { Close, ConfirmationNumber, FilterList } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { CardTitle, useDebounce } from '@sierralabs/react-material-ui';

import styled from 'styled-components';
import AppSnackbar from '../../../common/AppSnackbar';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import StatusLabel from '../../../common/StatusLabel';
import JiraLink from '../../jira-connect/components/JiraLink';
import JiraRenderedField from '../../jira-connect/components/JiraRenderedField';
import JiraIssueCreateDialog from '../../jira-connect/dialogs/JiraIssueCreateDialog';
import {
  useFindJiraIssues,
  useGetJiraIssuesByIds
} from '../../jira-connect/JiraConnect.hooks';
import {
  JiraIssue,
  JiraIssueType
} from '../../jira-connect/JiraConnect.interface';
import JiraIssueTypePicker from '../../jira-connect/pickers/JiraIssueTypePicker';
import JiraProjectPicker from '../../jira-connect/pickers/JiraProjectPicker';
import { useJiraServerContext } from '../../jira-server/jira-server.context';
import { useFindJiraServerIssues } from '../../jira-server/jira-server.hooks';
import { JiraFieldConfig } from '../../jira-server/jira-server.interface';
import { convertJiraStatus } from '../../jira/Jira.hooks';
import { Task } from '../Task.interface';
import {
  useCreateTaskJiraIssue,
  useDeleteTaskJiraIssue,
  useFindTaskJiraIssue
} from '../TaskJira.hooks';
import { TaskJiraIssue } from '../TaskJiraIssue.interface';

const RenderOption = styled.div`
  .issue-type {
    display: inline-flex;
    position: relative;
    padding-left: 32px;
    padding-right: 10px;
    margin-right: 3px;

    img {
      top: 3px;
      left: 8px;
      position: absolute;
      height: 18px;
      aspect-ratio: 1;
    }

    span {
      font-weight: 600;
    }
  }

  .summary {
    display: inline;
    margin: 0;
  }
`;

export const StyledMenu = styled(Menu)`
  ul {
    padding: 0;

    .MuiTypography-root {
      padding: 10px 10px 0 10px;
    }

    .MuiCardContent-root {
      padding: 10px;
    }

    .MuiCardActions-root {
      padding: 4px 10px;
    }
  }
`;

const TaskJiraIssueAssignCard: React.FC<{
  task: Task;
  jiraFieldColumns?: JiraFieldConfig[];
  entity?: { title: string; description?: string };
  compact?: boolean;
  onChange?: (taskJiraIssues: TaskJiraIssue[]) => void;
}> = props => {
  const { task, entity, compact, onChange } = props;
  const [search, setSearch] = useState('');
  const [projectId, setProjectId] = useState<string>();
  const [issueType, setIssueType] = useState<JiraIssueType>();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [openCreateJiraIssueDialog, setOpenCreateJiraIssueDialog] =
    useState(false);
  const [didCreateIssue, setDidCreateIssue] = useState<{
    open: boolean;
    key?: string;
  }>({ open: false });
  const [selectedIssue, setSelectedIssue] = useState<JiraIssue | null>(null);
  const [issueToUnlink, setIssueToUnlink] = useState<
    TaskJiraIssue | undefined
  >();
  const debouncedSearch = useDebounce(search || '', 300);
  const { isJiraServer, baseUrl } = useJiraServerContext();
  const [{ data: jiraIssues, isLoading }, findIssues] = useFindJiraIssues();
  // NOTE: jira server is deprecated
  // const { jiraServerIssues, loadingJiraServerIssues, findJiraServerIssues } =
  //   useFindJiraServerIssues();

  const jiraFieldColumns = useMemo(() => {
    // always show issue type column (for all environments)
    const columns = [
      { fieldKey: 'issuetype', type: 'text', title: 'Issue Type' }
    ] as JiraFieldConfig[];
    columns.push(...(props.jiraFieldColumns || []));
    return columns;
  }, [props.jiraFieldColumns]);

  useEffect(() => {
    findIssues({
      limit: 100,
      title: debouncedSearch,
      filter: { project: projectId, issueType: issueType?.id }
    });
  }, [debouncedSearch, issueType, projectId, findIssues]);

  const { createdTaskJiraIssue, creatingTaskJiraIssue, createTaskJiraIssue } =
    useCreateTaskJiraIssue();
  const { deletedJiraIssue, deleteTaskJiraIssue } = useDeleteTaskJiraIssue();
  const taskId = task.id || 0;
  const { taskJiraIssues, findTaskJiraIssues } = useFindTaskJiraIssue(taskId);
  // fetch when task.status gets updated after creating Jira issue on transition
  useEffect(
    () => findTaskJiraIssues(),
    [findTaskJiraIssues, task.status, createdTaskJiraIssue, deletedJiraIssue]
  );
  const [{ data: updatedJiraIssues }, getIds] = useGetJiraIssuesByIds();
  // check if jira summary has changed (always load latest summary from Jira)
  useEffect(() => {
    if (taskJiraIssues) {
      getIds(taskJiraIssues.map(i => i.key));
    }
  }, [taskJiraIssues, getIds]);
  // get the summary field from jira data in case our local cache is outdated
  const mergedTaskJiraIssues = useMemo(() => {
    if (!taskJiraIssues || !updatedJiraIssues) {
      return taskJiraIssues;
    }
    taskJiraIssues.forEach(issue => {
      const jiraIssue = updatedJiraIssues.issues.find(
        (i: any) => i.key === issue.key
      );
      issue.summary = jiraIssue?.fields.summary || issue.summary;
      issue.status = jiraIssue?.fields.status?.name || '';
    });
    onChangeRef.current?.(taskJiraIssues);
    return taskJiraIssues;
  }, [taskJiraIssues, updatedJiraIssues]);

  // remove selectedIssue on select to re-render field
  useEffect(() => {
    selectedIssue && setSelectedIssue(null);
  }, [selectedIssue]);

  const filterFields = (
    <>
      <Grid item xs={12} sm={compact ? 12 : 6} md={compact ? 12 : 3}>
        <JiraProjectPicker
          value={projectId}
          textFieldProps={{ size: 'small', placeholder: 'Project' }}
          onChange={id => {
            setProjectId(id);
            // remove issue type when changing project
            const issueTypeProjectId = issueType?.scope?.project?.id;
            if (id && issueTypeProjectId && id !== issueTypeProjectId) {
              setIssueType(undefined);
            }
          }}
        />
      </Grid>
      <Grid item xs={12} sm={compact ? 12 : 6} md={compact ? 12 : 3}>
        <JiraIssueTypePicker
          value={issueType?.id}
          projectId={projectId}
          textFieldProps={{ size: 'small', placeholder: 'Issue Type' }}
          onChange={value => setIssueType(value?.issueType)}
        />
      </Grid>
    </>
  );

  return (
    <Card>
      <CardTitle>
        <ConfirmationNumber />
        Linked Jira Issues
      </CardTitle>
      <CardContent>
        <Grid container spacing={1} alignItems={compact ? 'center' : undefined}>
          <Grid
            item
            style={compact ? { flex: 1 } : undefined}
            // disable the grid sizing to force this picker to use all available space
            // and push the filter icon to the right
            {...(!compact && { xs: 12, sm: 12, md: 6 })}
          >
            <Autocomplete<JiraIssue>
              fullWidth
              clearOnBlur
              loading={isLoading}
              value={selectedIssue}
              disabled={creatingTaskJiraIssue}
              options={jiraIssues?.data || []}
              getOptionLabel={issue => `${issue.key}: ${issue.fields.summary}`}
              renderOption={issue => (
                <RenderOption>
                  <div className='issue-type'>
                    {!!issue.fields.issuetype.iconUrl && (
                      <img
                        src={issue.fields.issuetype.iconUrl}
                        alt='issue-icon'
                      />
                    )}
                    <span>{issue.fields.issuetype.name}</span>
                  </div>
                  <div className='summary'>
                    {issue.key}: {issue.fields.summary}
                  </div>
                </RenderOption>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  variant='outlined'
                  size='small'
                  placeholder='Link a Jira Issue'
                />
              )}
              onInputChange={(_, value) => setSearch(value)}
              onChange={(_, issue) => {
                setSelectedIssue(issue);
                if (!issue) {
                  return;
                }
                if (!taskJiraIssues?.some(i => i.key === issue.key)) {
                  createTaskJiraIssue({
                    taskId,
                    key: issue.key,
                    summary: issue.fields.summary,
                    status: issue.fields.status.name,
                    issue: issue
                  });
                }
              }}
            />
          </Grid>
          {/* Directly show the filter fields if not compact */}
          {!compact ? (
            filterFields
          ) : (
            <Grid item>
              <IconButton
                size='small'
                onClick={event => setAnchorEl(event.currentTarget)}
              >
                <FilterList />
              </IconButton>
              <StyledMenu
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
              >
                <CardTitle>
                  <FilterList />
                  Filter
                </CardTitle>
                <CardContent>
                  <Grid container spacing={1}>
                    {filterFields}
                  </Grid>
                </CardContent>
                <CardActions>
                  <div style={{ flex: 1 }}></div>
                  <Button
                    size='small'
                    variant='text'
                    onClick={() => setAnchorEl(null)}
                  >
                    Close
                  </Button>
                </CardActions>
              </StyledMenu>
            </Grid>
          )}
        </Grid>
      </CardContent>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Status</TableCell>
              {jiraFieldColumns?.map(column => (
                <TableCell key={column.fieldKey}>{column.title}</TableCell>
              ))}
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mergedTaskJiraIssues?.map(taskJiraIssue => (
              <TableRow key={taskJiraIssue.id}>
                <TableCell>
                  <JiraLink
                    isJiraServer={isJiraServer}
                    issue={taskJiraIssue}
                    baseUrl={baseUrl}
                  />
                </TableCell>
                <TableCell style={{ whiteSpace: 'normal' }}>
                  {taskJiraIssue.summary}
                </TableCell>
                <TableCell>
                  <StatusLabel
                    status={convertJiraStatus(
                      taskJiraIssue.status || 'Backlog'
                    )}
                  />
                </TableCell>
                {jiraFieldColumns?.map(column => (
                  <TableCell
                    key={`cell_${column.fieldKey}_${taskJiraIssue.id}`}
                  >
                    <JiraRenderedField
                      key={`jireRenderedField_${column.fieldKey}_${taskJiraIssue.id}`}
                      issue={taskJiraIssue.issue}
                      config={column}
                      isJiraServer={isJiraServer}
                      baseUrl={baseUrl}
                    />
                  </TableCell>
                ))}
                <TableCell>
                  <IconButton
                    size='small'
                    onClick={() => setIssueToUnlink(taskJiraIssue)}
                  >
                    <Close />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CardActions>
        <Button
          color='primary'
          variant='outlined'
          onClick={() => setOpenCreateJiraIssueDialog(true)}
        >
          Create Jira Issue
        </Button>
      </CardActions>

      <ConfirmDialog
        open={!!issueToUnlink}
        confirmLabel='Confirm'
        title={`Unlink Issue ${issueToUnlink?.key || '?'}`}
        message='Are you sure you want to unlink this issue?'
        onCancel={() => setIssueToUnlink(undefined)}
        onConfirm={() => {
          issueToUnlink && deleteTaskJiraIssue(issueToUnlink.id);
          setIssueToUnlink(undefined);
        }}
      />
      <JiraIssueCreateDialog
        open={openCreateJiraIssueDialog}
        entity={{
          taskId,
          title: entity?.title || task.name,
          description: entity?.description || task.description
        }}
        onCancel={() => setOpenCreateJiraIssueDialog(false)}
        onCreate={(issue, shouldLink) => {
          setOpenCreateJiraIssueDialog(false);
          setDidCreateIssue({ open: true, key: issue.key });
          if (shouldLink) {
            createTaskJiraIssue({
              taskId,
              key: issue.key,
              summary: issue.fields.summary,
              status: issue.fields.status.name,
              issue: issue
            });
          }
        }}
      />

      <AppSnackbar
        severity='success'
        open={didCreateIssue.open}
        onClose={() => setDidCreateIssue(value => ({ ...value, open: false }))}
      >
        Created Jira Issue: {didCreateIssue.key}
      </AppSnackbar>
    </Card>
  );
};

export default TaskJiraIssueAssignCard;
